import { EmailData, SplitDocumentsControls } from './email-viewer-types';
import { SplitDocumentsSection } from './email-thread-utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { PDFViewer } from '@/components/common/pdf-viewer';
import { ExcelDocViewer } from '@/components/xlsx-viewer/ExcelDocViewer';
import { isExcelFile } from './utils';
import { ImageWithSmartScaling } from '@/components/common/ImageWithSmartScaling';
import { T } from 'gt-react';

interface ViewerAttachment {
  id: string;
  documentId?: string;
  fileName: string;
  sizeBytes: number;
  mimeType?: string;
  s3Url: string;
}

function getAllAttachments(emailData: EmailData): ViewerAttachment[] {
  return [
    ...(emailData.attachments ?? []),
    ...(emailData.documentAssociations ?? []).map((association) => ({
      id: association.id,
      documentId: association.document.id,
      fileName:
        association.document.originalFileName || association.document.name,
      sizeBytes: association.document.fileSizeBytes ?? 0,
      mimeType: association.document.mimeType,
      s3Url: association.document.url,
    })),
  ];
}

function isPdfAttachment(attachment: ViewerAttachment): boolean {
  return (
    attachment.mimeType === 'application/pdf' ||
    attachment.fileName?.toLowerCase().endsWith('.pdf')
  );
}

export function EmailAttachment({
  emailData,
  onRefreshData,
  splitDocuments,
}: {
  emailData: EmailData;
  onRefreshData?: () => void;
  splitDocuments?: SplitDocumentsControls;
}) {
  const allAttachments = getAllAttachments(emailData).sort((a, b) => {
    const aIsPdf = isPdfAttachment(a);
    const bIsPdf = isPdfAttachment(b);
    if (aIsPdf && !bIsPdf) return -1;
    if (!aIsPdf && bIsPdf) return 1;
    return 0;
  });

  const splitDocumentsSection = splitDocuments ? (
    <SplitDocumentsSection
      count={emailData.splitAttachmentsCount ?? 0}
      attachments={emailData.splitAttachments?.map((attachment) => ({
        id: attachment.id,
        documentId: attachment.id,
        fileName: attachment.fileName,
        sizeBytes: attachment.sizeBytes,
        mimeType: attachment.mimeType,
        s3Url: attachment.s3Url,
      }))}
      show={splitDocuments.show}
      onToggle={splitDocuments.onToggle}
      isLoading={splitDocuments.isLoading}
      onRefreshData={onRefreshData}
    />
  ) : null;

  if (!allAttachments.length) {
    return (
      <Card>
        <CardContent className="flex flex-col justify-center items-center p-8">
          <span className="text-xs text-gray-400 italic tracking-wide opacity-20">
            <T context="email viewer">No attachments found</T>
          </span>
          {splitDocumentsSection}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="p-1 space-y-2">
      {allAttachments.map((attachment) => (
        <Card key={attachment.id} onClick={(e) => e.stopPropagation()}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              {/* eslint-disable i18next/no-literal-string -- file size display with KB unit abbreviation */}
              <div className="text-sm font-medium break-words">
                {attachment.fileName} (
                {(attachment.sizeBytes / 1024).toFixed(2)} KB)
              </div>
              {/* eslint-enable i18next/no-literal-string */}
              <Button
                variant="link"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(attachment.s3Url, '_blank');
                }}
                className="h-auto p-2"
              >
                <ArrowSquareOutIcon className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Content based on file type */}
            {attachment.mimeType?.startsWith('image/') ? (
              <ImageWithSmartScaling
                src={attachment.s3Url}
                alt={attachment.fileName}
                fileName={attachment.fileName}
                sizeBytes={attachment.sizeBytes}
              />
            ) : isPdfAttachment(attachment) ? (
              <PDFViewer
                url={attachment.s3Url}
                pageMode="single"
                onUrlExpired={onRefreshData}
              />
            ) : isExcelFile(attachment) ? (
              <ExcelDocViewer
                url={attachment.s3Url}
                fileName={attachment.fileName}
                documentId={attachment.documentId}
              />
            ) : (
              <a
                href={attachment.s3Url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                <T context="action, download a file">Download File</T>
              </a>
            )}
          </CardContent>
        </Card>
      ))}
      {splitDocumentsSection}
    </div>
  );
}
